import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import AdminHubNav from "../components/AdminHubNav";
import { api, formatApiErrorDetail } from "../lib/api";
import { BetaDocsCallouts } from "./BetaDocs";
import {
  Plus, Copy, Check, Trash2, ArrowLeft, ShieldCheck, ShieldOff,
  Loader, AlertCircle, Mail, Send, Tag, Zap, FileText,
} from "lucide-react";

const BETA_URL = window.location.origin;

function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso);
    const d = (Date.now() - t.getTime()) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
    return t.toLocaleDateString();
  } catch {
    return "—";
  }
}

/**
 * ActivityBadge — at-a-glance "is this tester engaged?" pill.
 *
 *   🟢 Active 24h · 12 events     when events_24h > 0
 *   🟡 Idle · last 3d ago          when there's activity history but nothing in 24h
 *   🟡 Logged in 2d ago            when there's no activity but a recent login
 *                                  (covers pre-fix users where event tracking was broken)
 *   ⚫ No activity yet              when the tester has zero events AND zero logins
 *
 * Renders nothing extra — just a single line below the invite code.
 */
function ActivityBadge({ lastAt, events24h, lastLoginAt }) {
  const e24 = events24h || 0;
  let bg = "#E2E8F0", color = "#64748B", dot = "#94A3B8", label = "No activity yet", title = "This tester has not logged any events or logged in.";
  if (e24 > 0) {
    bg = "#DCFCE7"; color = "#14532D"; dot = "#16A34A";
    label = `Active 24h · ${e24} event${e24 === 1 ? "" : "s"}`;
    title = `${e24} event${e24 === 1 ? "" : "s"} in the last 24 hours. Last seen ${relTime(lastAt || lastLoginAt)}.`;
  } else if (lastAt) {
    bg = "#FEF3C7"; color = "#78350F"; dot = "#D97706";
    label = `Idle · last ${relTime(lastAt)}`;
    title = `No activity in the last 24h. Last activity ${relTime(lastAt)}.`;
  } else if (lastLoginAt) {
    bg = "#FEF3C7"; color = "#78350F"; dot = "#D97706";
    label = `Logged in ${relTime(lastLoginAt)}`;
    title = `Logged in but no in-app activity captured (likely from before activity tracking was fixed). Login: ${relTime(lastLoginAt)}.`;
  }
  return (
    <div
      className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono-rm"
      style={{ background: bg, color, border: "1px solid " + dot }}
      title={title}
      data-testid="beta-activity-badge"
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: dot }}
        aria-hidden="true"
      />
      {label}
    </div>
  );
}

export default function BetaInvitesAdmin() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create form
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState(""); // "" = unlimited, "1" = single-use, etc.
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");

  // Bulk add panel
  const [bulkInput, setBulkInput] = useState("");
  const [bulkMaxUses, setBulkMaxUses] = useState("");
  const [bulkAutoSend, setBulkAutoSend] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // {created:[], existing:[], failed:[]}

  // Single-add: opt-in auto-send (default true so admins don't forget)
  const [autoSend, setAutoSend] = useState(true);
  // Single-add: when the email already has an active invite, force-revoke +
  // reissue a fresh code in the same click. Default off so the duplicate-guard
  // still works for normal flows.
  const [forceReissue, setForceReissue] = useState(false);

  // Per-row "send invite email" state — { [inviteId]: 'sending' | 'sent' | 'error:<msg>' }
  const [sendingMap, setSendingMap] = useState({});
  // Independent send-state map for the 'Welcome letter' button per row.
  const [welcomeMap, setWelcomeMap] = useState({});
  // Independent send-state map for the 'Send both' one-click combo button.
  const [bothMap, setBothMap] = useState({});
  // Send-state map for the "Nudge to come back" button. Surfaces only on
  // rows whose tester has zero activity events — i.e. the ⚫ "No activity"
  // bucket — so admins can re-engage quiet invitees in one click.
  const [nudgeMap, setNudgeMap] = useState({});
  const [wrapMap, setWrapMap] = useState({});

  // Admin sandbox — "Test the beta as a tester"
  const [sandboxRunning, setSandboxRunning] = useState(false);
  const [sandbox, setSandbox] = useState(null); // {email, code, url}
  const [sandboxCopied, setSandboxCopied] = useState("");

  // Resend sender-domain verification status — drives a green/red banner at
  // the top of the page so admins know at-a-glance whether invite emails
  // will actually deliver.
  const [senderStatus, setSenderStatus] = useState(null);

  // Activity filter & sort for the invite list. Powers the engagement
  // dashboard so the founder can quickly see who's active vs ghosting.
  //   filter: "all" | "active" | "idle" | "none"
  //   sort  : "newest" | "active" | "seen"
  const [activityFilter, setActivityFilter] = useState("all");
  const [activitySort, setActivitySort] = useState("newest");

  const loadSenderStatus = async () => {
    try {
      const { data } = await api.get("/admin/email/sender-status");
      setSenderStatus(data);
    } catch {
      setSenderStatus({ verified: false, reason: "Couldn't check Resend status." });
    }
  };

  useEffect(() => { loadSenderStatus(); }, []);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/beta/invites");
      setInvites(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Filter + sort the invite list for the engagement dashboard. Computed
  // each render — list is admin-only and capped at 500.
  const invitesView = React.useMemo(() => {
    const visible = invites.filter((it) => {
      const e24 = it.events_24h || 0;
      const hasLogin = !!(it.last_activity_at || it.last_login_at);
      if (activityFilter === "active") return e24 > 0;
      if (activityFilter === "idle") return e24 === 0 && hasLogin;
      if (activityFilter === "none") return e24 === 0 && !hasLogin;
      return true;
    });
    return [...visible].sort((a, b) => {
      if (activitySort === "active") return (b.events_24h || 0) - (a.events_24h || 0);
      if (activitySort === "seen") {
        const ta = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
        const tb = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
        return tb - ta;
      }
      const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
      const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return cb - ca;
    });
  }, [invites, activityFilter, activitySort]);

  const create = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setCreating(true);
    try {
      const body = {
        email: email.trim(),
        label: label.trim(),
        note: note.trim(),
        auto_send: autoSend,
        force_reissue: forceReissue,
      };
      const parsedMax = parseInt(maxUses, 10);
      if (!Number.isNaN(parsedMax) && parsedMax >= 1) body.max_uses = parsedMax;
      const { data } = await api.post("/admin/beta/invites", body);
      // Surface the email-send result so admin knows if the letter went out.
      if (autoSend && data?.email_status && !data.email_status.ok) {
        setError(`Invite created, but email failed to send: ${data.email_status.error}. You can retry from the row's Email button.`);
      }
      // Surface the reissue audit so admin sees which code was rotated.
      if (data?.reissued_from?.code) {
        setError(`Reissued: revoked previous code ${data.reissued_from.code}, issued new code ${data.code}.`);
      }
      setEmail(""); setLabel(""); setNote(""); setMaxUses(""); setForceReissue(false);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (invite) => {
    try {
      await api.patch(`/admin/beta/invites/${invite.id}`, { active: !invite.active });
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (invite) => {
    if (!window.confirm(`Permanently delete the invite for ${invite.email}? This cannot be undone.`)) return;
    let purge = false;
    if (invite.account_id || invite.redeemed_at) {
      purge = window.confirm(
        `${invite.email} has a registered account.\n\n` +
        `OK = ALSO permanently delete their account and ALL data (resume, analyses, credits, history) so re-adding them starts 100% fresh.\n` +
        `Cancel = delete the invite only (their account and data stay).`
      );
    }
    try {
      await api.delete(`/admin/beta/invites/${invite.id}${purge ? "?purge_account=1" : ""}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Danger zone — purge an orphaned account by email (covers users whose
  // invite was already deleted but whose account + data still linger).
  const [purgeEmail, setPurgeEmail] = useState("");
  const [purgeMsg, setPurgeMsg] = useState(null); // { ok, text }
  const [purging, setPurging] = useState(false);

  const purgeByEmail = async () => {
    const email = purgeEmail.trim().toLowerCase();
    if (!email) return;
    if (!window.confirm(
      `Permanently delete the account ${email} and ALL of its data (resume, analyses, credits, history, invites)?\n\nThis cannot be undone.`
    )) return;
    setPurging(true);
    setPurgeMsg(null);
    try {
      const { data } = await api.delete(`/admin/users/by-email/${encodeURIComponent(email)}`);
      const total = Object.values(data.deletions || {}).reduce((a, b) => a + (b || 0), 0);
      setPurgeMsg({ ok: true, text: `✓ ${email} fully removed — ${total} records purged. Re-adding them now starts fresh.` });
      setPurgeEmail("");
      load();
    } catch (e) {
      setPurgeMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
    setPurging(false);
  };

  // Send (or resend) the invite letter via Resend to the tester's email.
  const sendEmail = async (invite) => {
    setSendingMap((m) => ({ ...m, [invite.id]: "sending" }));
    try {
      await api.post(`/admin/beta/invites/${invite.id}/send-email`);
      setSendingMap((m) => ({ ...m, [invite.id]: "sent" }));
      setTimeout(() => setSendingMap((m) => {
        const next = { ...m }; delete next[invite.id]; return next;
      }), 2500);
      load();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setSendingMap((m) => ({ ...m, [invite.id]: `error:${detail}` }));
      setError(`Email send failed for ${invite.email}: ${detail}`);
    }
  };

  // Query Resend for the live delivery status of a specific invite. Persists
  // the fresh status server-side so the pill is up-to-date on next load.
  const [deliveryChecking, setDeliveryChecking] = useState({}); // {inviteId: true}
  const checkDelivery = async (invite) => {
    setDeliveryChecking((m) => ({ ...m, [invite.id]: true }));
    try {
      const { data } = await api.get(`/admin/beta/invites/${invite.id}/delivery-status`);
      // Patch the invite in-memory so the pill reflects the fresh status
      // without a full list refetch (nice for admins triaging bounces).
      setInvites((prev) => prev.map((i) => i.id === invite.id
        ? { ...i, email_last_status: data.status,
            email_last_error: data.reason || i.email_last_error }
        : i));
      if (data.status === "bounced" || data.status === "complained") {
        setError(`${invite.email} · ${data.status.toUpperCase()} — ${data.reason || "Recipient rejected the message"}`);
      } else if (data.status === "delivery_delayed") {
        setError(`${invite.email} · Delivery delayed — usually means the recipient's mail server is temporarily unreachable. Resend will keep retrying for ~72h.`);
      } else {
        setError("");
      }
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(`Delivery check failed: ${detail}`);
    } finally {
      setDeliveryChecking((m) => {
        const next = { ...m }; delete next[invite.id]; return next;
      });
    }
  };

  // Send (or resend) the 'Meet Resmo — the Resume Monster' welcome letter
  // via Resend. Independent from the gate-invite letter — separate telemetry
  // fields on the invite doc so the admin can see both delivery statuses
  // independently in the row.
  const sendWelcome = async (invite) => {
    setWelcomeMap((m) => ({ ...m, [invite.id]: "sending" }));
    try {
      await api.post(`/admin/beta/invites/${invite.id}/send-welcome`);
      setWelcomeMap((m) => ({ ...m, [invite.id]: "sent" }));
      setTimeout(() => setWelcomeMap((m) => {
        const next = { ...m }; delete next[invite.id]; return next;
      }), 2500);
      load();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setWelcomeMap((m) => ({ ...m, [invite.id]: `error:${detail}` }));
      setError(`Welcome letter send failed for ${invite.email}: ${detail}`);
    }
  };

  // One-click full onboarding: fire the gate-invite letter AND the welcome
  // letter back-to-back. Calls the two existing endpoints sequentially so
  // each one's telemetry is recorded independently; if the first fails the
  // second is still attempted (a missing invite letter shouldn't block the
  // welcome). Errors surface in the page-level error banner with which
  // sub-send failed.
  const sendBoth = async (invite) => {
    setBothMap((m) => ({ ...m, [invite.id]: "sending" }));
    const failures = [];
    try {
      await api.post(`/admin/beta/invites/${invite.id}/send-email`);
    } catch (e) {
      const d = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      failures.push(`invite letter (${d})`);
    }
    try {
      await api.post(`/admin/beta/invites/${invite.id}/send-welcome`);
    } catch (e) {
      const d = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      failures.push(`welcome letter (${d})`);
    }
    if (failures.length === 0) {
      setBothMap((m) => ({ ...m, [invite.id]: "sent" }));
      setTimeout(() => setBothMap((m) => {
        const next = { ...m }; delete next[invite.id]; return next;
      }), 2500);
    } else {
      setBothMap((m) => ({ ...m, [invite.id]: `error:${failures.join(" + ")}` }));
      setError(`Send-both for ${invite.email} hit issues: ${failures.join(" + ")}`);
    }
    load();
  };

  // Send a "come back to Resmo" nudge to a quiet tester. Only surfaces on
  // ⚫ "No activity yet" rows. Reuses the existing render_nudge_email
  // template server-side and stamps nudge_* telemetry fields on the invite.
  const sendNudge = async (invite) => {
    setNudgeMap((m) => ({ ...m, [invite.id]: "sending" }));
    try {
      await api.post(`/admin/beta/invites/${invite.id}/send-nudge`);
      setNudgeMap((m) => ({ ...m, [invite.id]: "sent" }));
      setTimeout(() => setNudgeMap((m) => {
        const next = { ...m }; delete next[invite.id]; return next;
      }), 2500);
      load();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setNudgeMap((m) => ({ ...m, [invite.id]: `error:${detail}` }));
      setError(`Nudge send failed for ${invite.email}: ${detail}`);
    }
  };

  const sendWrapup = async (invite) => {
    setWrapMap((m) => ({ ...m, [invite.id]: "sending" }));
    try {
      await api.post(`/admin/beta/invites/${invite.id}/send-wrapup`);
      setWrapMap((m) => ({ ...m, [invite.id]: "sent" }));
      setTimeout(() => setWrapMap((m) => {
        const next = { ...m }; delete next[invite.id]; return next;
      }), 2500);
      load();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setWrapMap((m) => ({ ...m, [invite.id]: `error:${detail}` }));
      setError(`Wrap-up send failed for ${invite.email}: ${detail}`);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* clipboard blocked */ }
  };

  const copyInvite = async (it) => {
    const msg = `Hi —

You're in. Welcome to the Resmo private beta.

Resmo is a career intelligence companion — it tailors your resume to any job, runs salary intel, researches roles you've never held, builds interview prep banks, and (the new fun part) hosts live mock interviews with coach feedback and a final scorecard.

────────────────────────────────────────
HOW TO ACCESS
────────────────────────────────────────
  Beta URL:    ${BETA_URL}
  Email:       ${it.email}
  Invite code: ${it.code}

  1. Open the URL.
  2. Type your email and paste the invite code above.
  3. Click Unlock, then sign up using the SAME email.
     (Registration is locked to the invite email — this is on purpose.)

────────────────────────────────────────
WHAT TO EXPECT INSIDE
────────────────────────────────────────
  • Tailor a Resume        Paste a Job Description → fit score + tailored resume
  • Skill Lab              Rate skills → salary range + adjacent roles
  • Research a Role        Salary tiers, skills, market outlook
  • Job Search (live)      Live Adzuna listings + one-click tailor
  • Interview Prep         Auto-generated question bank
  • Mock Interview (new!)  Live Q&A with rubric scoring + coach notes
  • Stepping Stones        Your interview growth journal

Pick Resmo (warm, mid-career) or ProMo (terse, senior+) — switch any time
in Account → Mode. The full tester guide with screenshots is in the app
once you're in.

────────────────────────────────────────
A FEW GROUND RULES
────────────────────────────────────────
  • Private beta. Please don't share the URL, code, or screenshots.
    (Every page is watermarked with your email + timestamp — all access
    is logged.)
  • Reply to this email with anything broken, confusing, or magical.
    Screenshots help. We read every one.

Thanks for testing.
— The Resmo Team`;
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(it.code + "-msg");
      setTimeout(() => setCopied(""), 1800);
    } catch { /* ignore */ }
  };

  // Bulk add: parses pasted text (one email per line, or commas) and creates
  // invites for each. Reports created vs already-existing vs failed.
  const runBulkAdd = async () => {
    setError("");
    // Extract emails — accept newlines, commas, semicolons, spaces between.
    const emails = (bulkInput || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.includes("@") && s.includes("."));
    if (emails.length === 0) {
      setError("Paste at least one email address.");
      return;
    }
    const uniqueEmails = Array.from(new Set(emails));
    setBulkRunning(true);
    setBulkResults(null);
    const created = [];
    const existing = [];
    const failed = [];
    const parsedMax = parseInt(bulkMaxUses, 10);
    const max_uses = (!Number.isNaN(parsedMax) && parsedMax >= 1) ? parsedMax : undefined;

    for (const em of uniqueEmails) {
      try {
        const body = { email: em, label: "", note: "Bulk-added", auto_send: bulkAutoSend };
        if (max_uses) body.max_uses = max_uses;
        const { data } = await api.post("/admin/beta/invites", body);
        created.push(data);
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (detail.includes("already exists")) {
          existing.push(em);
        } else {
          failed.push({ email: em, reason: detail || e.message });
        }
      }
    }

    // For existing emails, fetch their current code so we can show invite messages
    if (existing.length > 0) {
      try {
        const { data } = await api.get("/admin/beta/invites");
        const byEmail = Object.fromEntries(data.map((x) => [x.email, x]));
        for (const em of existing) {
          if (byEmail[em]) created.push({ ...byEmail[em], _wasExisting: true });
        }
      } catch { /* ignore */ }
    }

    setBulkResults({ created, existingCount: existing.length, failed });
    setBulkRunning(false);
    load();
  };

  const copyAllBulkMessages = async () => {
    if (!bulkResults?.created?.length) return;
    const blocks = bulkResults.created.map((it) =>
`════════════════════════════════════════
TO: ${it.email}
════════════════════════════════════════

Hi —

You're in. Welcome to the Resmo private beta.

Resmo is a career intelligence companion — it tailors your resume to any
job, runs salary intel, researches roles you've never held, builds
interview prep banks, and hosts live mock interviews with coach feedback.

HOW TO ACCESS
  Beta URL:    ${BETA_URL}
  Email:       ${it.email}
  Invite code: ${it.code}

  1. Open the URL.
  2. Type your email and paste the invite code above.
  3. Click Unlock, then sign up using the SAME email.
     (Registration is locked to the invite email.)

WHAT TO EXPECT INSIDE
  • Tailor a Resume   • Skill Lab          • Research a Role
  • Job Search (live) • Interview Prep     • Mock Interview (new!)
  • Stepping Stones (your interview growth journal)

Pick Resmo (warm, mid-career) or ProMo (terse, senior+) at sign-up.
The full tester guide is in the app once you're in.

GROUND RULES
  • Private beta. Please don't share the URL, code, or screenshots.
    Every page is watermarked; all access is logged.
  • Reply to this email with feedback. Screenshots help.

Thanks for testing.
— The Resmo Team`).join("\n\n\n");
    const header = `BETA INVITE LETTERS · ${bulkResults.created.length} testers\n` +
                   `Copy/paste each block into a separate email.\n\n\n`;
    try {
      await navigator.clipboard.writeText(header + blocks);
      setCopied("bulk-all");
      setTimeout(() => setCopied(""), 2000);
    } catch { /* ignore */ }
  };

  const activeCount = invites.filter((i) => i.active && !(i.max_uses != null && (i.use_count || 0) >= i.max_uses)).length;
  const exhaustedCount = invites.filter((i) => i.active && i.max_uses != null && (i.use_count || 0) >= i.max_uses).length;
  const revokedCount = invites.filter((i) => !i.active).length;
  const redeemedCount = invites.filter((i) => i.redeemed_at).length;

  // Email delivery stats — drives the new status card row + retry pipeline.
  const deliveredCount = invites.filter((i) => i.email_last_status === "delivered").length;
  const failedInvites = invites.filter((i) => i.email_last_status === "failed" && i.active);
  const failedCount = failedInvites.length;
  const neverSentCount = invites.filter((i) => i.active && !i.email_last_status).length;

  // Retry every failed invite in series. Stops on first non-error (i.e. config
  // problem like unverified domain) to avoid hammering Resend.
  const [retryAllRunning, setRetryAllRunning] = useState(false);
  const retryAllFailed = async () => {
    if (failedInvites.length === 0) return;
    setRetryAllRunning(true);
    setError("");
    let ok = 0;
    let fail = 0;
    for (const it of failedInvites) {
      try {
        await api.post(`/admin/beta/invites/${it.id}/send-email`);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setRetryAllRunning(false);
    if (fail > 0 && ok === 0) {
      setError(`All ${fail} retries failed. Most likely cause: your Resend sender domain isn't verified. Open https://resend.com/domains to fix.`);
    }
    load();
  };

  // Create a single-use throwaway invite + open the gate URL with pre-filled email & code.
  // The code auto-expires after the first unlock; nothing for the admin to clean up.
  const generateSandboxInvite = async () => {
    setSandboxRunning(true);
    setError("");
    setSandbox(null);
    try {
      const ts = Date.now();
      const sandboxEmail = `sandbox-${ts}@resmo-sandbox.app`;
      const { data } = await api.post("/admin/beta/invites", {
        email: sandboxEmail,
        label: "Admin sandbox preview",
        note: "Auto-generated by 'Test as a tester' — single-use, expires on first unlock.",
        max_uses: 1,
      });
      const previewUrl = `${BETA_URL}/beta?email=${encodeURIComponent(sandboxEmail)}&code=${encodeURIComponent(data.code)}`;
      setSandbox({ email: sandboxEmail, code: data.code, url: previewUrl });
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSandboxRunning(false);
    }
  };

  const copySandboxField = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setSandboxCopied(key);
      setTimeout(() => setSandboxCopied(""), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 pb-32">
        <button onClick={() => navigate("/")} className="rm-tag mb-3" data-testid="beta-admin-back">
          <ArrowLeft size={14} /> Back
        </button>
        <AdminHubNav active="beta" />
        <div className="rm-tag mb-3">Admin · Beta</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">
          Beta <span className="rm-text-orange">invites.</span>
        </h1>
        <p className="text-base md:text-lg max-w-2xl mb-8" style={{ color: "#475569" }}>
          Each tester gets a unique invite code bound to their email. Revoke any code
          instantly to cut off access without affecting others.
        </p>

        {/* Resend domain verification status — must be green for emails to deliver. */}
        {senderStatus && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3 flex-wrap"
            style={{
              background: senderStatus.verified ? "#F0FDF4" : "#FEF2F2",
              borderColor: "#0F1B2D",
            }}
            data-testid="sender-domain-status"
          >
            <span
              style={{
                width: 12, height: 12, borderRadius: 6, marginTop: 5,
                background: senderStatus.verified ? "#10B981" : "#DC2626",
                border: "2px solid #0F1B2D",
                flexShrink: 0,
              }}
            />
            <div className="flex-1 min-w-[260px]">
              <div className="rm-label" style={{ color: senderStatus.verified ? "#065F46" : "#991B1B" }}>
                Resend sender · {senderStatus.sender_email || "(not configured)"}
              </div>
              <div className="font-display text-base leading-tight mt-1"
                   style={{ color: senderStatus.verified ? "#065F46" : "#991B1B" }}>
                {senderStatus.verified
                  ? "Verified — invite emails will deliver ✓"
                  : "Domain not verified — emails will NOT deliver yet"}
              </div>
              {senderStatus.reason && (
                <div className="text-xs mt-1 font-mono-rm" style={{ color: "#475569" }}>
                  {senderStatus.reason}
                </div>
              )}
            </div>
            {!senderStatus.verified && (
              <a
                href={senderStatus.resend_url || "https://resend.com/domains"}
                target="_blank"
                rel="noopener noreferrer"
                className="rm-btn rm-btn-primary text-sm"
                data-testid="sender-domain-fix-link"
              >
                Fix at resend.com →
              </a>
            )}
            <button
              type="button"
              onClick={loadSenderStatus}
              className="rm-btn rm-btn-ghost text-xs px-2 py-1"
              title="Re-check Resend status"
              data-testid="sender-domain-recheck"
            >
              Re-check
            </button>
          </div>
        )}

        {/* DMARC recommendation — non-blocking but strongly improves inbox
            placement. Only surface when the domain is verified (otherwise
            the sender pill above already tells the admin what to fix first)
            AND DMARC is missing. */}
        {senderStatus?.verified && senderStatus?.dmarc?.checked && !senderStatus.dmarc.present && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3 flex-wrap"
            style={{ background: "#FFF7ED", borderColor: "#0F1B2D" }}
            data-testid="sender-dmarc-warning"
          >
            <span style={{
              width: 12, height: 12, borderRadius: 6, marginTop: 5,
              background: "#F59E0B", border: "2px solid #0F1B2D", flexShrink: 0,
            }} />
            <div className="flex-1 min-w-[260px]">
              <div className="rm-label" style={{ color: "#92400E" }}>
                · DMARC not set for {senderStatus.domain || "your sender domain"}
              </div>
              <div className="font-display text-base leading-tight mt-1" style={{ color: "#92400E" }}>
                Gmail / Yahoo / Outlook may spam-fold your invites
              </div>
              <div className="text-xs mt-1 font-mono-rm" style={{ color: "#475569" }}>
                Add this DNS TXT record at your registrar (Namecheap / Cloudflare / GoDaddy):
              </div>
              <pre
                className="text-[11px] mt-2 rounded p-2 whitespace-pre-wrap break-all"
                style={{ background: "#0F1B2D", color: "#FFC145", fontFamily: "'Courier New',monospace" }}
                data-testid="sender-dmarc-record"
              >
                {`Host:   _dmarc.${senderStatus.domain}\nType:   TXT\nValue:  v=DMARC1; p=none; rua=mailto:${senderStatus.sender_email || 'you@yourinbox.com'}`}
              </pre>
              <div className="text-[11px] mt-2" style={{ color: "#475569" }}>
                Start with <code>p=none</code> (monitor-only, safe). After ~30 days
                of clean reports, move to <code>p=quarantine</code>.
              </div>
            </div>
            <a
              href="https://dmarcian.com/dmarc-inspector/"
              target="_blank" rel="noopener noreferrer"
              className="rm-btn rm-btn-primary text-sm"
              data-testid="sender-dmarc-inspect"
              style={{ background: "#F59E0B", borderColor: "#0F1B2D", color: "#0F1B2D" }}
            >
              Learn about DMARC →
            </a>
          </div>
        )}
        {senderStatus?.verified && senderStatus?.dmarc?.present && (
          <div className="text-[10px] font-mono-rm uppercase tracking-widest mb-4"
               style={{ color: "#065F46" }}
               data-testid="sender-dmarc-ok">
            · DMARC verified on {senderStatus.domain} ✓
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="rm-card-static p-4" data-testid="beta-stat-active">
            <div className="rm-label">Active</div>
            <div className="font-display text-3xl">{activeCount}</div>
          </div>
          <div className="rm-card-static p-4" style={{ background: "#FFFBEB" }} data-testid="beta-stat-redeemed">
            <div className="rm-label">Redeemed</div>
            <div className="font-display text-3xl">{redeemedCount}</div>
          </div>
          <div className="rm-card-static p-4" style={{ background: "#FEF2F2" }} data-testid="beta-stat-exhausted">
            <div className="rm-label">Exhausted</div>
            <div className="font-display text-3xl">{exhaustedCount}</div>
          </div>
          <div className="rm-card-static p-4" style={{ background: "#F1F5F9" }} data-testid="beta-stat-revoked">
            <div className="rm-label">Revoked</div>
            <div className="font-display text-3xl">{revokedCount}</div>
          </div>
        </div>

        {/* Email delivery status — green / red / gray strip */}
        <div className="rm-card-static p-4 mb-8" data-testid="beta-email-status-strip"
             style={{ background: "white", borderColor: "#0F1B2D" }}>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="rm-label mb-2">Invite email delivery</div>
              <div className="flex items-center gap-4 flex-wrap font-mono-rm text-xs">
                <span className="inline-flex items-center gap-2" data-testid="beta-email-delivered">
                  <span style={{ width: 10, height: 10, borderRadius: 5, background: "#10B981",
                                 border: "2px solid #0F1B2D", display: "inline-block" }} />
                  <strong style={{ fontSize: 16 }}>{deliveredCount}</strong>
                  <span style={{ color: "#475569" }}>delivered</span>
                </span>
                <span className="inline-flex items-center gap-2" data-testid="beta-email-failed">
                  <span style={{ width: 10, height: 10, borderRadius: 5,
                                 background: failedCount > 0 ? "#EF4444" : "#E2E8F0",
                                 border: "2px solid #0F1B2D", display: "inline-block" }} />
                  <strong style={{ fontSize: 16, color: failedCount > 0 ? "#991B1B" : "#0F1B2D" }}>
                    {failedCount}
                  </strong>
                  <span style={{ color: "#475569" }}>failed</span>
                </span>
                <span className="inline-flex items-center gap-2" data-testid="beta-email-never-sent">
                  <span style={{ width: 10, height: 10, borderRadius: 5, background: "#94A3B8",
                                 border: "2px solid #0F1B2D", display: "inline-block" }} />
                  <strong style={{ fontSize: 16 }}>{neverSentCount}</strong>
                  <span style={{ color: "#475569" }}>not sent yet</span>
                </span>
              </div>
            </div>
            {failedCount > 0 && (
              <button
                type="button"
                onClick={retryAllFailed}
                disabled={retryAllRunning}
                className="rm-btn rm-btn-primary text-sm"
                style={{ background: "#EF4444", borderColor: "#0F1B2D" }}
                data-testid="beta-retry-all-failed"
                title={`Retry sending all ${failedCount} failed invite emails`}
              >
                {retryAllRunning
                  ? <><Loader size={14} className="animate-spin" /> Retrying {failedCount}…</>
                  : <><Send size={14} /> Retry all {failedCount} failed</>}
              </button>
            )}
          </div>
        </div>

        <BetaDocsCallouts />

        {/* Test as a tester — admin sandbox preview */}
        <div
          className="rm-card-static p-5 md:p-6 mb-6"
          style={{ background: "#0F1B2D", color: "white", borderColor: "#FFC145",
                   boxShadow: "5px 5px 0 0 #FFC145" }}
          data-testid="sandbox-block"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={16} style={{ color: "#FFC145" }} />
                <div className="rm-label" style={{ color: "#FFC145" }}>Test as a tester</div>
              </div>
              <h2 className="font-display text-xl uppercase leading-tight">
                Walk the full <span className="rm-text-orange">unlock flow</span>
              </h2>
              <p className="text-sm mt-2" style={{ color: "#CBD5E1" }}>
                Generates a single-use throwaway invite code, opens the gate URL with email and code already pre-filled.
                Best run in an <strong>incognito / private window</strong> so it doesn't interfere with your admin session.
                The code auto-expires after the first unlock — no cleanup needed.
              </p>
            </div>
            <button
              type="button"
              onClick={generateSandboxInvite}
              disabled={sandboxRunning}
              className="rm-btn rm-btn-primary"
              data-testid="sandbox-generate-btn"
            >
              {sandboxRunning
                ? <><Loader size={14} className="animate-spin" /> Minting…</>
                : <><Zap size={14} /> Generate sandbox invite</>}
            </button>
          </div>

          {sandbox && (
            <div
              className="p-4 mt-2 rounded-md"
              style={{ background: "rgba(255,193,69,0.08)", border: "2px dashed #FFC145" }}
              data-testid="sandbox-result"
            >
              <div className="rm-label mb-2" style={{ color: "#FFC145" }}>Your single-use sandbox invite</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                       style={{ color: "#94A3B8" }}>Throwaway email</div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono-rm text-xs px-2 py-1 flex-1 truncate"
                          style={{ background: "rgba(255,255,255,0.06)", color: "#FFC145",
                                   border: "1px solid rgba(255,193,69,0.3)" }}
                          data-testid="sandbox-email">
                      {sandbox.email}
                    </code>
                    <button onClick={() => copySandboxField("email", sandbox.email)}
                            className="rm-btn text-xs"
                            style={{ background: "white", color: "#0F1B2D", border: "2px solid #FFC145" }}
                            data-testid="sandbox-copy-email">
                      {sandboxCopied === "email" ? <><Check size={12} /> Copied</> : <><Copy size={12} /></>}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                       style={{ color: "#94A3B8" }}>Throwaway code</div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono-rm text-xs px-2 py-1 flex-1 truncate"
                          style={{ background: "rgba(255,255,255,0.06)", color: "#FFC145",
                                   border: "1px solid rgba(255,193,69,0.3)" }}
                          data-testid="sandbox-code">
                      {sandbox.code}
                    </code>
                    <button onClick={() => copySandboxField("code", sandbox.code)}
                            className="rm-btn text-xs"
                            style={{ background: "white", color: "#0F1B2D", border: "2px solid #FFC145" }}
                            data-testid="sandbox-copy-code">
                      {sandboxCopied === "code" ? <><Check size={12} /> Copied</> : <><Copy size={12} /></>}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a
                  href={sandbox.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rm-btn rm-btn-primary text-sm"
                  data-testid="sandbox-open-new-tab"
                >
                  <Zap size={14} /> Open pre-filled gate in new tab →
                </a>
                <button
                  type="button"
                  onClick={() => copySandboxField("url", sandbox.url)}
                  className="rm-btn text-sm"
                  style={{ background: "white", color: "#0F1B2D", border: "2px solid #FFC145" }}
                  data-testid="sandbox-copy-url"
                >
                  {sandboxCopied === "url" ? <><Check size={14} /> Copied URL</> : <><Copy size={14} /> Copy URL</>}
                </button>
              </div>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-3"
                   style={{ color: "#94A3B8" }}>
                Tip: paste the URL into an incognito window for a true first-time-tester perspective.
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 mb-6 rounded-md font-mono-rm text-sm flex items-start gap-2"
               style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* QUICK BULK ADD — fastest path: paste emails, one per line */}
        <div
          className="rm-card-static p-6 md:p-7 mb-6"
          style={{ background: "#FFFBEB" }}
          data-testid="beta-bulk-panel"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center"
                 style={{ background: "#FFC145", color: "#0F1B2D", border: "3px solid #0F1B2D" }}>
              <Zap size={20} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="rm-label">Fast path</div>
              <h2 className="font-display text-xl uppercase leading-tight">
                Quick add (bulk) — paste emails.
              </h2>
            </div>
          </div>
          <p className="text-sm mb-3" style={{ color: "#475569" }}>
            One email per line. We'll skip duplicates and report any failures.
            Default access: unlimited uses (good for trusted testers).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-9">
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder={`alex@acme.com\njordan@beta.io\nsam@example.com`}
                rows={5}
                className="w-full px-3 py-2.5 font-mono-rm text-sm resize-y"
                style={{
                  background: "white",
                  border: "3px solid #0F1B2D",
                  borderRadius: 6,
                  minHeight: 110,
                }}
                data-testid="beta-bulk-textarea"
                disabled={bulkRunning}
              />
            </div>
            <div className="md:col-span-3 flex flex-col gap-2">
              <div>
                <label className="rm-label block mb-1.5">Max uses</label>
                <select
                  value={bulkMaxUses}
                  onChange={(e) => setBulkMaxUses(e.target.value)}
                  className="w-full px-3 py-2.5 font-mono-rm text-sm"
                  style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                  data-testid="beta-bulk-max-uses"
                  disabled={bulkRunning}
                >
                  <option value="">Unlimited</option>
                  <option value="1">1 — single-use</option>
                  <option value="2">2 uses</option>
                  <option value="3">3 uses</option>
                  <option value="5">5 uses</option>
                  <option value="10">10 uses</option>
                </select>
              </div>
              <button
                type="button"
                onClick={runBulkAdd}
                disabled={bulkRunning}
                className="rm-btn rm-btn-primary justify-center mt-auto"
                data-testid="beta-bulk-submit"
              >
                {bulkRunning ? (
                  <>
                    <Loader size={14} className="animate-spin" /> Generating…
                  </>
                ) : (
                  <>
                    <Zap size={14} /> Generate all
                  </>
                )}
              </button>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer font-mono-rm mt-3"
                 style={{ color: "#0F1B2D" }}>
            <input
              type="checkbox"
              checked={bulkAutoSend}
              onChange={(e) => setBulkAutoSend(e.target.checked)}
              data-testid="beta-bulk-auto-send"
              style={{ width: 16, height: 16, accentColor: "#FF6A00" }}
              disabled={bulkRunning}
            />
            <Send size={13} className="rm-text-orange" />
            <span>Email each invite letter automatically (via Resend) — no copy-paste needed</span>
          </label>

          {bulkResults && (
            <div
              className="mt-4 p-4 rounded-md"
              style={{ background: "white", border: "3px solid #0F1B2D" }}
              data-testid="beta-bulk-results"
            >
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="rm-label">Results</div>
                <div className="flex gap-2">
                  <span className="rm-tag text-[10px]" style={{ background: "#10B981", color: "white", borderColor: "#0F1B2D" }}>
                    {bulkResults.created.length} ready
                  </span>
                  {bulkResults.existingCount > 0 && (
                    <span className="rm-tag text-[10px]" style={{ background: "#FFC145" }}>
                      {bulkResults.existingCount} already had a code
                    </span>
                  )}
                  {bulkResults.failed.length > 0 && (
                    <span className="rm-tag text-[10px]" style={{ background: "#EF4444", color: "white", borderColor: "#0F1B2D" }}>
                      {bulkResults.failed.length} failed
                    </span>
                  )}
                </div>
              </div>

              {bulkResults.created.length > 0 && (
                <>
                  <div className="space-y-2 mb-3 max-h-80 overflow-auto"
                       data-testid="beta-bulk-results-list">
                    {bulkResults.created.map((it) => (
                      <div
                        key={it.code}
                        className="p-2 px-3 rounded font-mono-rm text-xs flex items-center justify-between gap-2"
                        style={{ background: "#F8FAFC", border: "2px solid #E2E8F0" }}
                      >
                        <div className="flex-1 truncate">
                          <span className="font-bold" style={{ color: "#0F1B2D" }}>{it.code}</span>
                          <span style={{ color: "#94A3B8" }}> · </span>
                          <span style={{ color: "#475569" }}>{it.email}</span>
                          {it._wasExisting && (
                            <span className="ml-2 text-[9px] uppercase tracking-widest"
                                  style={{ color: "#92400E" }}>· existing</span>
                          )}
                        </div>
                        <button
                          onClick={() => copyInvite(it)}
                          className="rm-btn rm-btn-ghost text-[10px] px-2 py-1 flex-shrink-0"
                        >
                          {copied === it.code + "-msg" ? <Check size={11} /> : <Mail size={11} />}
                          {copied === it.code + "-msg" ? "Copied" : "Msg"}
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={copyAllBulkMessages}
                    className="rm-btn rm-btn-primary text-sm"
                    data-testid="beta-bulk-copy-all"
                  >
                    {copied === "bulk-all" ? <Check size={14} /> : <FileText size={14} />}
                    {copied === "bulk-all" ? "All copied!" : `Copy all ${bulkResults.created.length} messages`}
                  </button>
                </>
              )}

              {bulkResults.failed.length > 0 && (
                <div className="mt-3 pt-3 border-t-2 border-dashed" style={{ borderColor: "#FCA5A5" }}>
                  <div className="rm-label mb-2" style={{ color: "#991B1B" }}>Failures</div>
                  <ul className="text-xs space-y-1" style={{ color: "#991B1B" }}>
                    {bulkResults.failed.map((f, i) => (
                      <li key={i}>
                        <span className="font-mono-rm">{f.email}</span> — {f.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create form */}
        <div className="rm-card-static p-6 md:p-7 mb-8" data-testid="beta-create-form">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center"
                 style={{ background: "#FF6A00", color: "white", border: "3px solid #0F1B2D" }}>
              <Plus size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="rm-label">Issue a new invite</div>
              <h2 className="font-display text-xl uppercase leading-tight">Add a tester.</h2>
            </div>
          </div>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <label className="rm-label block mb-1.5">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tester@example.com"
                className="w-full px-3 py-2.5 font-mono-rm text-sm"
                style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                data-testid="beta-create-email"
                disabled={creating}
              />
            </div>
            <div className="md:col-span-2">
              <label className="rm-label block mb-1.5">Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Jane Smith"
                className="w-full px-3 py-2.5 font-mono-rm text-sm"
                style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                data-testid="beta-create-label"
                disabled={creating}
              />
            </div>
            <div className="md:col-span-2">
              <label className="rm-label block mb-1.5">Max uses</label>
              <select
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                className="w-full px-3 py-2.5 font-mono-rm text-sm"
                style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                data-testid="beta-create-max-uses"
                disabled={creating}
                title="How many times this code can be redeemed before it auto-revokes"
              >
                <option value="">Unlimited</option>
                <option value="1">1 — single-use</option>
                <option value="2">2 uses</option>
                <option value="3">3 uses</option>
                <option value="5">5 uses</option>
                <option value="10">10 uses</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="rm-label block mb-1.5">Note</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="cohort, source"
                className="w-full px-3 py-2.5 font-mono-rm text-sm"
                style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                data-testid="beta-create-note"
                disabled={creating}
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                type="submit"
                disabled={creating}
                className="rm-btn rm-btn-primary w-full justify-center"
                data-testid="beta-create-submit"
              >
                {creating ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                Generate
              </button>
            </div>
            <div className="md:col-span-12 -mt-1">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer font-mono-rm"
                     style={{ color: "#0F1B2D" }}>
                <input
                  type="checkbox"
                  checked={autoSend}
                  onChange={(e) => setAutoSend(e.target.checked)}
                  data-testid="beta-create-auto-send"
                  style={{ width: 16, height: 16, accentColor: "#FF6A00" }}
                />
                <Send size={13} className="rm-text-orange" />
                <span>Email the invite letter to the tester immediately (via Resend)</span>
              </label>
            </div>
            <div className="md:col-span-12">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer font-mono-rm"
                     style={{ color: "#0F1B2D" }}>
                <input
                  type="checkbox"
                  checked={forceReissue}
                  onChange={(e) => setForceReissue(e.target.checked)}
                  data-testid="beta-create-force-reissue"
                  style={{ width: 16, height: 16, accentColor: "#FF6A00" }}
                />
                <ShieldOff size={13} className="rm-text-orange" />
                <span>If this email already has an active invite, <strong>revoke it and reissue a fresh code</strong> (use when a tester lost their code).</span>
              </label>
            </div>
          </form>
        </div>

        {/* Invite list — engagement filter + sort */}
        {(() => {
          const buckets = { all: invites.length, active: 0, idle: 0, none: 0 };
          for (const it of invites) {
            if ((it.events_24h || 0) > 0) buckets.active += 1;
            else if (it.last_activity_at || it.last_login_at) buckets.idle += 1;
            else buckets.none += 1;
          }
          const filterTabs = [
            { id: "all", label: `All (${buckets.all})`, color: "#0F1B2D" },
            { id: "active", label: `🟢 Active 24h (${buckets.active})`, color: "#16A34A" },
            { id: "idle", label: `🟡 Idle (${buckets.idle})`, color: "#D97706" },
            { id: "none", label: `⚫ No activity (${buckets.none})`, color: "#64748B" },
          ];
          return (
            <div className="mb-3 flex flex-wrap gap-2 items-center justify-between">
              <div className="flex flex-wrap gap-1.5" data-testid="beta-activity-filter">
                {filterTabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActivityFilter(t.id)}
                    className="text-[11px] font-mono-rm px-2.5 py-1 rounded-full border-2"
                    style={{
                      background: activityFilter === t.id ? t.color : "white",
                      color: activityFilter === t.id ? "white" : t.color,
                      borderColor: t.color,
                      fontWeight: 700,
                    }}
                    data-testid={`beta-activity-filter-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <label className="text-[11px] font-mono-rm flex items-center gap-2" style={{ color: "#475569" }}>
                Sort by:
                <select
                  value={activitySort}
                  onChange={(e) => setActivitySort(e.target.value)}
                  className="rm-input text-[11px] py-1 px-2"
                  style={{ width: "auto" }}
                  data-testid="beta-activity-sort"
                >
                  <option value="newest">Newest invite</option>
                  <option value="active">Most active (24h)</option>
                  <option value="seen">Recently seen</option>
                </select>
              </label>
            </div>
          );
        })()}
        {loading ? (
          <div className="text-center py-10 font-mono-rm">Loading…</div>
        ) : invites.length === 0 ? (
          <div className="text-center py-10 font-mono-rm text-sm" style={{ color: "#475569" }}>
            No invites yet. Issue your first one above.
          </div>
        ) : invitesView.length === 0 ? (
          <div className="text-center py-10 font-mono-rm text-sm" style={{ color: "#475569" }}>
            No invites match this filter.
          </div>
        ) : (
          <div className="space-y-3" data-testid="beta-invites-list">
            {invitesView.map((it) => {
              const exhausted = it.max_uses != null && (it.use_count || 0) >= it.max_uses;
              const remaining = it.max_uses != null ? Math.max(0, it.max_uses - (it.use_count || 0)) : null;
              return (
              <div
                key={it.id}
                className="rm-card p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
                style={{
                  background: !it.active || exhausted ? "#F8FAFC" : "white",
                  opacity: !it.active ? 0.65 : exhausted ? 0.85 : 1,
                }}
                data-testid={`beta-invite-${it.code}`}
              >
                {/* Code + status */}
                <div className="md:col-span-3">
                  <div className="font-mono-rm text-base font-bold tracking-wider"
                       style={{ color: it.active && !exhausted ? "#0F1B2D" : "#94A3B8" }}>
                    {it.code}
                  </div>
                  <ActivityBadge lastAt={it.last_activity_at} events24h={it.events_24h} lastLoginAt={it.last_login_at} />
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <button
                      onClick={() => copyCode(it.code)}
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      data-testid={`beta-copy-code-${it.code}`}
                    >
                      {copied === it.code ? <Check size={11} /> : <Copy size={11} />}
                      {copied === it.code ? "Copied" : "Code"}
                    </button>
                    <button
                      onClick={() => copyInvite(it)}
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      data-testid={`beta-copy-msg-${it.code}`}
                    >
                      {copied === it.code + "-msg" ? <Check size={11} /> : <Mail size={11} />}
                      {copied === it.code + "-msg" ? "Copied" : "Invite msg"}
                    </button>
                    <button
                      onClick={() => sendEmail(it)}
                      disabled={!it.active || sendingMap[it.id] === "sending"}
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      style={{
                        background: sendingMap[it.id] === "sent" ? "#10B981" : "#FF6A00",
                        color: "white",
                        borderColor: "#0F1B2D",
                      }}
                      title={it.emailed_at ? `Last emailed ${relTime(it.emailed_at)} — click to resend` : "Email the invite letter to this tester"}
                      data-testid={`beta-email-${it.code}`}
                    >
                      {sendingMap[it.id] === "sending"
                        ? <><Loader size={11} className="animate-spin" /> Sending…</>
                        : sendingMap[it.id] === "sent"
                          ? <><Check size={11} /> Sent</>
                          : <><Send size={11} /> {it.emailed_at ? "Resend" : "Email"}</>}
                    </button>
                    {/* 'Welcome letter' — emails the 'Meet Resmo — your Resume Monster'
                        founding-tester letter as a Resend email body. Independent
                        from the gate-invite letter so admins can send both, or
                        just the warmer welcome on its own. */}
                    <button
                      onClick={() => sendWelcome(it)}
                      disabled={!it.active || welcomeMap[it.id] === "sending"}
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      style={{
                        background:
                          welcomeMap[it.id] === "sent"
                            ? "#10B981"
                            : "linear-gradient(135deg, #0F1B2D 0%, #1F3559 60%, #FF6A00 140%)",
                        color: "white",
                        borderColor: "#0F1B2D",
                      }}
                      title={
                        it.welcome_emailed_at
                          ? `Welcome letter sent ${relTime(it.welcome_emailed_at)} — click to resend`
                          : "Send the 'Meet Resmo, the Resume Monster' welcome letter via Resend"
                      }
                      data-testid={`beta-welcome-${it.code}`}
                    >
                      {welcomeMap[it.id] === "sending"
                        ? <><Loader size={11} className="animate-spin" /> Sending…</>
                        : welcomeMap[it.id] === "sent"
                          ? <><Check size={11} /> Sent</>
                          : <><Mail size={11} /> {it.welcome_emailed_at ? "Resend welcome" : "Welcome letter"}</>}
                    </button>
                    {/* 'Send both' — fires the gate-invite letter AND the
                        welcome letter back-to-back. Visually loud (gold accent)
                        so admins see this as the one-click full-onboarding
                        action distinct from sending just one. Disabled while
                        either sub-send is in progress to avoid duplicate
                        Resend hits. */}
                    <button
                      onClick={() => sendBoth(it)}
                      disabled={
                        !it.active ||
                        bothMap[it.id] === "sending" ||
                        sendingMap[it.id] === "sending" ||
                        welcomeMap[it.id] === "sending"
                      }
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      style={{
                        background:
                          bothMap[it.id] === "sent" ? "#10B981" : "#FFC145",
                        color: "#0F1B2D",
                        borderColor: "#0F1B2D",
                        fontWeight: 700,
                      }}
                      title="Send both letters: gate-invite + welcome letter via Resend, in one click"
                      data-testid={`beta-sendboth-${it.code}`}
                    >
                      {bothMap[it.id] === "sending"
                        ? <><Loader size={11} className="animate-spin" /> Sending both…</>
                        : bothMap[it.id] === "sent"
                          ? <><Check size={11} /> Both sent</>
                          : <><Zap size={11} /> Send both</>}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                    <span
                      className="rm-tag text-[10px]"
                      style={{
                        background: !it.active ? "#94A3B8" : exhausted ? "#EF4444" : "#10B981",
                        color: "white",
                        borderColor: "#0F1B2D",
                      }}
                      data-testid={`beta-status-${it.code}`}
                    >
                      {!it.active ? "REVOKED" : exhausted ? "EXHAUSTED" : "ACTIVE"}
                    </span>

                    {/* Email delivery status — colored dot + label + live
                        re-check button that queries Resend for the current
                        `last_event` (delivered / delivery_delayed / bounced /
                        complained / sent / opened). */}
                    {(() => {
                      const status = it.email_last_status;
                      // Meta table keyed on the raw Resend event so we can
                      // add new event types without changing the DOM.
                      const META = {
                        delivered:        { bg: "#D1FAE5", fg: "#065F46", dot: "#10B981", label: "Delivered" },
                        opened:           { bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6", label: "Opened" },
                        clicked:          { bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6", label: "Clicked" },
                        sent:             { bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B", label: "Sent · in-flight" },
                        delivery_delayed: { bg: "#FFEDD5", fg: "#9A3412", dot: "#F97316", label: "Delayed" },
                        bounced:          { bg: "#FEE2E2", fg: "#991B1B", dot: "#EF4444", label: "Bounced" },
                        complained:       { bg: "#FEE2E2", fg: "#991B1B", dot: "#EF4444", label: "Spam-flagged" },
                        failed:           { bg: "#FEE2E2", fg: "#991B1B", dot: "#EF4444", label: "Failed" },
                      };
                      const m = META[status] || { bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8", label: "Not sent" };
                      const checking = !!deliveryChecking[it.id];
                      const canCheck = !!it.emailed_at || !!it.email_send_id;
                      return (
                        <span
                          className="rm-tag text-[10px] inline-flex items-center gap-1"
                          style={{ background: m.bg, color: m.fg, borderColor: m.fg }}
                          title={
                            it.email_last_error
                              ? `${m.label} · ${it.email_last_error}`
                              : `${m.label}${it.emailed_at ? " · " + relTime(it.emailed_at) : ""}${it.email_send_id ? " · Resend ID " + it.email_send_id : ""}`
                          }
                          data-testid={`beta-email-status-${it.code}`}
                        >
                          <span style={{ width: 7, height: 7, borderRadius: 4,
                                         background: m.dot, display: "inline-block" }} />
                          {m.label}
                          {it.emailed_at && (
                            <span style={{ opacity: 0.7, marginLeft: 2 }}>· {relTime(it.emailed_at)}</span>
                          )}
                          {canCheck && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); checkDelivery(it); }}
                              disabled={checking}
                              className="ml-1 rounded inline-flex items-center"
                              style={{
                                border: `1px solid ${m.fg}`, padding: "0 4px",
                                background: "white", color: m.fg,
                                fontSize: 9, cursor: checking ? "wait" : "pointer",
                                lineHeight: "12px",
                              }}
                              title="Re-check delivery status against Resend"
                              data-testid={`beta-email-recheck-${it.code}`}
                            >
                              {checking ? "…" : "↻ Test"}
                            </button>
                          )}
                        </span>
                      );
                    })()}

                    {/* Welcome-letter delivery status — only renders once the
                        welcome letter has been attempted at least once, so the
                        row isn't cluttered for fresh invites. */}
                    {it.welcome_email_last_status && (() => {
                      const ws = it.welcome_email_last_status;
                      if (ws === "delivered") {
                        return (
                          <span
                            className="rm-tag text-[10px] inline-flex items-center gap-1"
                            style={{ background: "#FFFBEB", color: "#92400E", borderColor: "#D97706" }}
                            title={`Welcome letter delivered ${it.welcome_emailed_at ? relTime(it.welcome_emailed_at) : ""}${it.welcome_email_send_id ? " · Resend ID " + it.welcome_email_send_id : ""}`}
                            data-testid={`beta-welcome-status-${it.code}`}
                          >
                            <span style={{ width: 7, height: 7, borderRadius: 4, background: "#D97706",
                                           display: "inline-block" }} />
                            Welcome · {it.welcome_emailed_at ? relTime(it.welcome_emailed_at) : ""}
                          </span>
                        );
                      }
                      return (
                        <span
                          className="rm-tag text-[10px] inline-flex items-center gap-1"
                          style={{ background: "#FEE2E2", color: "#991B1B", borderColor: "#991B1B" }}
                          title={it.welcome_email_last_error || "Welcome letter send failed"}
                          data-testid={`beta-welcome-status-${it.code}`}
                        >
                          <span style={{ width: 7, height: 7, borderRadius: 4, background: "#EF4444",
                                         display: "inline-block" }} />
                          Welcome failed · {it.welcome_email_last_attempt_at ? relTime(it.welcome_email_last_attempt_at) : ""}
                        </span>
                      );
                    })()}

                    {it.account_id && (
                      <span className="rm-tag text-[10px]" style={{ background: "#FFC145" }}>
                        Registered
                      </span>
                    )}

                    {it.nda_status === "signed" && (
                      <span className="rm-tag text-[10px]"
                            style={{ background: "#D1FAE5", color: "#065F46", borderColor: "#0E5A4A" }}
                            title="Tester signed the NDA in-app"
                            data-testid={`beta-nda-status-${it.code}`}>
                        NDA ✓
                      </span>
                    )}
                    {it.nda_status === "pending" && (
                      <span className="rm-tag text-[10px]"
                            style={{ background: "#FEE2E2", color: "#991B1B", borderColor: "#991B1B" }}
                            title="Tester registered but hasn't signed the NDA wall yet — nudge them"
                            data-testid={`beta-nda-status-${it.code}`}>
                        NDA pending
                      </span>
                    )}
                    {it.nda_status === "exempt" && (
                      <span className="rm-tag text-[10px]"
                            style={{ background: "#F1F5F9", color: "#64748B", borderColor: "#CBD5E1" }}
                            title="Account created before the in-app NDA wall — covered by the old DocuSign process"
                            data-testid={`beta-nda-status-${it.code}`}>
                        NDA n/a
                      </span>
                    )}

                    {typeof it.beta_week === "number" && it.beta_week >= 1 && (
                      <span className="rm-tag text-[10px]"
                            style={it.beta_week > 4
                              ? { background: "#FEE2E2", color: "#991B1B", borderColor: "#991B1B" }
                              : { background: "#EFF6FF", color: "#1E40AF", borderColor: "#1E40AF" }}
                            title={it.beta_week > 4
                              ? `Past the 4-week beta window (week ${it.beta_week})`
                              : `Beta week ${it.beta_week} of 4`}
                            data-testid={`beta-week-${it.code}`}>
                        {it.beta_week > 4 ? `Wk ${it.beta_week} · over` : `Wk ${it.beta_week}/4`}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email + label */}
                <div className="md:col-span-4">
                  <div className="font-bold text-sm break-all">{it.email}</div>
                  {it.label && (
                    <div className="font-mono-rm text-xs mt-1" style={{ color: "#475569" }}>
                      <Tag size={10} className="inline mr-1" /> {it.label}
                    </div>
                  )}
                  {it.note && (
                    <div className="text-xs mt-1" style={{ color: "#475569" }}>
                      {it.note}
                    </div>
                  )}
                </div>

                {/* Usage stats */}
                <div className="md:col-span-3 font-mono-rm text-[11px]" style={{ color: "#475569" }}>
                  <div>
                    <span style={{ color: "#0F1B2D" }}>Uses:</span> {it.use_count || 0}
                    {it.max_uses != null && (
                      <span style={{ color: exhausted ? "#EF4444" : "#475569" }}>
                        {" "}/ {it.max_uses}
                        {!exhausted && remaining != null && ` (${remaining} left)`}
                      </span>
                    )}
                    {it.max_uses == null && (
                      <span style={{ color: "#94A3B8" }}> · unlimited</span>
                    )}
                  </div>
                  <div><span style={{ color: "#0F1B2D" }}>Created:</span> {relTime(it.created_at)}</div>
                  <div><span style={{ color: "#0F1B2D" }}>Redeemed:</span> {it.redeemed_at ? relTime(it.redeemed_at) : "—"}</div>
                  <div><span style={{ color: "#0F1B2D" }}>Last used:</span> {it.last_used_at ? relTime(it.last_used_at) : "—"}</div>
                  {it.last_used_ip && (
                    <div className="truncate"><span style={{ color: "#0F1B2D" }}>IP:</span> {it.last_used_ip}</div>
                  )}
                </div>

                {/* Actions */}
                <div className="md:col-span-2 flex flex-wrap gap-2 md:justify-end">
                  {/* Nudge — only on rows whose tester is in the ⚫ "No
                      activity" bucket. Sends a re-engagement email via
                      Resend (reuses render_nudge_email template). */}
                  {it.active && (it.events_24h || 0) === 0 && !it.last_activity_at && !it.last_login_at && (
                    <button
                      onClick={() => sendNudge(it)}
                      disabled={nudgeMap[it.id] === "sending"}
                      className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                      style={{
                        background: nudgeMap[it.id] === "sent" ? "#10B981" : "#FFC145",
                        color: "#0F1B2D",
                        borderColor: "#0F1B2D",
                        fontWeight: 700,
                      }}
                      title={it.nudge_last_sent_at
                        ? `Nudge last sent ${relTime(it.nudge_last_sent_at)} — click to send again`
                        : "Send a 'come back to Resmo' email"}
                      data-testid={`beta-nudge-${it.code}`}
                    >
                      <Zap size={13} />
                      {nudgeMap[it.id] === "sending"
                        ? "Sending…"
                        : nudgeMap[it.id] === "sent"
                          ? "Sent!"
                          : it.nudge_send_count
                            ? `Nudge again (${it.nudge_send_count})`
                            : "Nudge"}
                    </button>
                  )}
                  {/* Beta wrap-up — surfaces once the tester reaches week 4
                      of their window. Thank-you + final-feedback email. */}
                  {it.active && it.account_id && (it.beta_week || 0) >= 4 && (
                    <button
                      onClick={() => sendWrapup(it)}
                      disabled={wrapMap[it.id] === "sending"}
                      className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                      style={{
                        background: wrapMap[it.id] === "sent" || it.wrapup_send_count ? "#10B981" : "#0E5A4A",
                        color: "white",
                        borderColor: "#0F1B2D",
                        fontWeight: 700,
                      }}
                      title={it.wrapup_last_sent_at
                        ? `Wrap-up last sent ${relTime(it.wrapup_last_sent_at)} — click to send again`
                        : "Send the beta wrap-up email (thanks + final feedback ask)"}
                      data-testid={`beta-wrapup-${it.code}`}
                    >
                      <Send size={13} />
                      {wrapMap[it.id] === "sending"
                        ? "Sending…"
                        : wrapMap[it.id] === "sent"
                          ? "Sent!"
                          : it.wrapup_send_count
                            ? `Wrap-up again (${it.wrapup_send_count})`
                            : "Beta wrap-up"}
                    </button>
                  )}
                  <button
                    onClick={() => toggle(it)}
                    className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                    title={it.active ? "Revoke this invite" : "Re-activate this invite"}
                    data-testid={`beta-toggle-${it.code}`}
                  >
                    {it.active ? <ShieldOff size={13} /> : <ShieldCheck size={13} />}
                    {it.active ? "Revoke" : "Restore"}
                  </button>
                  <button
                    onClick={() => remove(it)}
                    className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                    title="Delete forever"
                    data-testid={`beta-delete-${it.code}`}
                    style={{ color: "#991B1B" }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}

        {/* ── Danger zone — full account purge by email ─────────────────── */}
        <div className="rm-card-static p-4 mt-8" style={{ borderColor: "#B91C1C" }}
             data-testid="admin-purge-card">
          <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "#B91C1C", fontWeight: 700 }}>
            · Danger zone — delete a user account
          </div>
          <p className="text-xs mb-3" style={{ color: "#475569" }}>
            Permanently removes the account, resume, analyses, credits, history, and any
            leftover invites for an email — so re-adding them starts 100% fresh. Use this
            for testers whose invite was already deleted but whose old data still shows up.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={purgeEmail}
              onChange={(e) => setPurgeEmail(e.target.value)}
              placeholder="tester@email.com"
              className="rm-input text-sm"
              style={{ maxWidth: 320 }}
              data-testid="admin-purge-email-input"
            />
            <button
              onClick={purgeByEmail}
              disabled={purging || !purgeEmail.trim()}
              className="rm-btn text-xs px-4 py-2 disabled:opacity-50"
              style={{ background: "#B91C1C", color: "white", border: "2px solid #0F1B2D", fontWeight: 700 }}
              data-testid="admin-purge-submit"
            >
              <Trash2 size={13} /> {purging ? "Purging…" : "Delete account + all data"}
            </button>
          </div>
          {purgeMsg && (
            <div className="font-mono-rm text-xs mt-3 p-2 rounded"
                 style={purgeMsg.ok
                   ? { background: "#D1FAE5", border: "1.5px solid #0E5A4A", color: "#0E5A4A" }
                   : { background: "#FEF2F2", border: "1.5px solid #EF4444", color: "#991B1B" }}
                 data-testid="admin-purge-result">
              {purgeMsg.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
